<template>
	<div
		ref="progress-container"
		class="progress-container"
		:class="{disabled}"
		role="progressbar"
		:aria-valuenow="progressBarWidth"
		aria-valuemin="0"
		aria-valuemax="100"
		:aria-label="'Video progress'"
		tabindex="0"
		@click="onProgressBarClick"
		@keydown="onKeyDown"
	>
		<!-- red progress bar -->
		<div class="progress-bar" :style="{width: progressBarWidth + '%'}" />
		<!-- markers, if any  -->
		<template v-if="markersWithPercentage.length && !disabled">
			<div
				v-for="marker in markersWithPercentage"
				:key="marker.seconds"
				class="absolute transform -translate-x-1/2"
				:style="{left: `min(${marker.percentage}%, calc(100% - 8px))`, top: '-20px'}"
			>
				<BaseTooltip placement="top">
					<button
						:ref="`marker-${marker.seconds}`"
						class="marker"
						@click.stop="onMarkerClick(marker.seconds)"
					>
						<BaseIcon v-if="$store.isExtension" name="marker-timeline" class="w-24 h-24" />
						<img v-else :src="markerImageSrc" class="w-24 h-24" />
					</button>
					<template #text>
						<!-- eslint-disable-next-line vue/no-v-html -->
						<div class="truncate" v-html="marker.text" />
					</template>
				</BaseTooltip>
			</div>
		</template>
	</div>
</template>

<script>
import analytics from 'shared/utils/analytics'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'VideoPlayerProgressBar',
	props: {
		disabled: Boolean,
	},
	setup() {
		return {sessionStore: useSessionStore()}
	},
	data() {
		return {
			markerImageSrc: new URL('../img/marker-timeline.png', import.meta.url),
		}
	},
	computed: {
		duration() {
			return this.sessionStore.videoDurationSeconds
		},
		time() {
			return this.sessionStore.currentTimeSeconds
		},
		progressBarWidth() {
			if (!this.duration) return 0
			const percentage = (this.time / this.duration) * 100
			return Math.min(Math.max(percentage, 0), 100)
		},
		markersWithPercentage() {
			const markers = this.sessionStore.markers['session-description']
			if (!markers) return []
			return Object.values(markers).map(marker => {
				const percentage = (marker.seconds / this.duration) * 100
				return {
					...marker,
					percentage,
				}
			})
		},
	},
	mounted() {
		window.addEventListener('timestamp-mouse-over', this.onTimestampMouseOver)
		window.addEventListener('timestamp-mouse-out', this.onTimestampMouseOut)
	},
	unmounted() {
		window.removeEventListener('timestamp-mouse-over', this.onTimestampMouseOver)
		window.removeEventListener('timestamp-mouse-out', this.onTimestampMouseOut)
	},
	methods: {
		onProgressBarClick(event) {
			if (this.disabled) return
			// Track video player progress bar interaction for user
			if (this.$route.path.includes('how-to-record')) {
				analytics.track('Demo Page Video Interaction')
			}
			const progressContainer = this.$refs['progress-container']
			const rect = progressContainer.getBoundingClientRect()
			const position = (event.pageX - rect.left) / progressContainer.offsetWidth
			const newCurrentTime = position * this.duration
			this.sessionStore.seek(newCurrentTime)
		},
		onMarkerClick(seconds) {
			if (this.disabled) return
			this.sessionStore.seek(seconds)
		},
		onTimestampMouseOver(event) {
			if (this.disabled) return
			const seconds = event.detail
			const marker = this.$refs[`marker-${seconds}`]
			if (marker) {
				marker[0].classList.add('hover')
			}
		},
		onTimestampMouseOut(event) {
			if (this.disabled) return
			const seconds = event.detail
			const marker = this.$refs[`marker-${seconds}`]
			if (marker) {
				marker[0].classList.remove('hover')
			}
		},
		onKeyDown(event) {
			if (this.disabled) return
			if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
				const adjustment = event.key === 'ArrowRight' ? 1 : -1
				const newTime = this.time + (adjustment * this.duration) / 100
				const seekSeconds = Math.min(Math.max(newTime, 0), this.duration)
				this.sessionStore.seek(seekSeconds)
			}
		},
	},
}
</script>

<style scoped>
.progress-container {
	height: 6px;
	position: relative;
	background-color: var(--gray-500);
	transition: height 0.2s ease;
}

.progress-container:not(.disabled) {
	cursor: pointer;
}

.progress-container:not(.disabled):focus {
	height: 10px;
}

.progress-container:not(.disabled):focus-visible {
	outline: 2px solid var(--blue-500);
}

.progress-bar {
	position: absolute;
	inset: 0;
	background-color: var(--red-500);
	transition: width 0.2s ease;
}

/* height transition for hover effect */
.progress-container:not(.disabled):hover {
	height: 10px;
}

.marker {
	transition: transform 0.2s ease;
	transform-origin: bottom center;
}

.marker.hover,
.marker:hover,
.marker:focus {
	transform: scale(1.5);
}
</style>
